/* eslint-disable newline-before-return */
import { CONFIG } from "@/components/FormEngine/config";
import { RawField } from "@/components/FormEngine/types";
import { isObject } from "@/components/FormEngine/utils";

// ========================================
// TYPES ET INTERFACES
// ========================================

/**
 * Représente un nœud feuille trouvé dans l'arbre
 * Contient les données du champ et son chemin complet
 */
type LeafNode = {
  /** Les données du champ (nœud feuille) */
  data: RawField;
  /** Le chemin depuis la racine jusqu'à ce nœud */
  path: string[];
};

/**
 * Type pour une valeur de propriété dans un objet
 */
type PropertyValue = unknown;

// ========================================
// VÉRIFICATION DE LA STRUCTURE
// ========================================

/**
 * Vérifie si un objet contient des propriétés qui sont elles-mêmes des objets
 * (ou des tableaux d'objets), en ignorant certaines clés spécifiques
 *
 * Un objet sans enfants objets est considéré comme une "feuille" de l'arbre
 *
 * @param node - Le nœud à examiner
 * @returns true si le nœud contient au moins un objet enfant, false sinon
 *
 * @example
 * hasChildObjects({ name: "John", age: 30 })           // false (que des primitives)
 * hasChildObjects({ user: { name: "John" } })          // true (contient un objet)
 * hasChildObjects({ items: [{ id: 1 }] })              // true (contient un tableau d'objets)
 * hasChildObjects({ values: [1, 2, 3] })               // false (tableau de primitives)
 */
const hasChildObjects = (node: unknown): boolean => {
  // Vérifier que le nœud est bien un objet
  if (!isObject(node)) {
    return false;
  }

  // Parcourir toutes les propriétés de l'objet
  for (const [propertyKey, propertyValue] of Object.entries(node)) {
    // Ignorer les clés spécifiées dans la configuration
    if (shouldIgnoreProperty(propertyKey)) {
      continue;
    }

    // Vérifier si la propriété contient des objets
    if (propertyContainsObjects(propertyValue)) {
      return true;
    }
  }

  return false;
};

/**
 * Détermine si une propriété doit être ignorée lors du parcours
 *
 * @param propertyKey - La clé de la propriété
 * @returns true si la propriété doit être ignorée
 */
function shouldIgnoreProperty(propertyKey: string): boolean {
  return CONFIG.IGNORED_KEYS.includes(propertyKey as any);
}

/**
 * Vérifie si une valeur de propriété contient des objets
 * (directement ou dans un tableau)
 *
 * @param propertyValue - La valeur à vérifier
 * @returns true si la valeur contient des objets
 */
function propertyContainsObjects(propertyValue: PropertyValue): boolean {
  // Cas 1: C'est un tableau
  if (Array.isArray(propertyValue)) {
    return arrayContainsObjects(propertyValue);
  }

  // Cas 2: C'est directement un objet
  return isObject(propertyValue);
}

/**
 * Vérifie si un tableau contient au moins un objet
 *
 * @param array - Le tableau à vérifier
 * @returns true si le tableau contient au moins un objet
 */
function arrayContainsObjects(array: unknown[]): boolean {
  return array.some((item) => isObject(item));
}

// ========================================
// PARCOURS DE L'ARBRE
// ========================================

/**
 * Parcourt récursivement un arbre d'objets et retourne tous les nœuds "feuilles"
 *
 * Un nœud feuille est un objet qui ne contient pas d'autres objets comme enfants
 * (il peut contenir des primitives : string, number, boolean, etc.)
 *
 * Protection contre les références circulaires avec WeakSet
 * Protection contre la récursion infinie avec MAX_RECURSION_DEPTH
 *
 * @param rootNode - Le nœud racine à partir duquel commencer le parcours
 * @param currentPath - Le chemin actuel depuis la racine (utilisé en interne)
 * @param visited - Ensemble des objets déjà visités (protection contre les cycles)
 * @returns Liste de tous les nœuds feuilles trouvés avec leur chemin
 *
 * @example
 * const tree = {
 *   user: {
 *     profile: {
 *       name: "John",
 *       age: 30
 *     },
 *     settings: {
 *       theme: "dark"
 *     }
 *   }
 * };
 *
 * findLeafObjects(tree);
 * // Retourne:
 * // [
 * //   { data: { name: "John", age: 30 }, path: ["user", "profile"] },
 * //   { data: { theme: "dark" }, path: ["user", "settings"] }
 * // ]
 */
export const findLeafObjects = (
  rootNode: unknown,
  currentPath: string[] = [],
  visited = new WeakSet<object>(),
): LeafNode[] => {
  // Initialiser la liste des feuilles trouvées
  const foundLeaves: LeafNode[] = [];

  // Vérifications de sécurité
  if (!canProcessNode(rootNode, currentPath, visited)) {
    return foundLeaves;
  }

  // Marquer ce nœud comme visité (on sait que c'est un objet après canProcessNode)
  markNodeAsVisited(rootNode as object, visited);

  // Vérifier si c'est une feuille
  if (isLeafNode(rootNode)) {
    const leafNode = createLeafNode(rootNode as RawField, currentPath);
    foundLeaves.push(leafNode);
    return foundLeaves;
  }

  // C'est un nœud interne : parcourir ses enfants
  const childrenLeaves = processNodeChildren(
    rootNode as object,
    currentPath,
    visited,
  );
  foundLeaves.push(...childrenLeaves);

  return foundLeaves;
};

// ========================================
// FONCTIONS DE VALIDATION
// ========================================

/**
 * Vérifie si un nœud peut être traité
 *
 * @param node - Le nœud à vérifier
 * @param path - Le chemin actuel
 * @param visited - L'ensemble des nœuds visités
 * @returns true si le nœud peut être traité
 */
function canProcessNode(
  node: unknown,
  path: string[],
  visited: WeakSet<object>,
): boolean {
  // Le nœud doit être un objet
  if (!isObject(node)) {
    return false;
  }

  // Le nœud ne doit pas avoir déjà été visité (protection contre les cycles)
  if (visited.has(node)) {
    return false;
  }

  // La profondeur ne doit pas dépasser la limite
  if (isMaxDepthExceeded(path)) {
    return false;
  }

  return true;
}

/**
 * Vérifie si la profondeur maximale de récursion est dépassée
 *
 * @param path - Le chemin actuel
 * @returns true si la profondeur maximale est dépassée
 */
function isMaxDepthExceeded(path: string[]): boolean {
  return path.length > CONFIG.MAX_RECURSION_DEPTH;
}

/**
 * Marque un nœud comme visité
 *
 * @param node - Le nœud à marquer
 * @param visited - L'ensemble des nœuds visités
 */
function markNodeAsVisited(node: object, visited: WeakSet<object>): void {
  visited.add(node);
}

/**
 * Vérifie si un nœud est une feuille (pas d'enfants objets)
 *
 * @param node - Le nœud à vérifier
 * @returns true si c'est une feuille
 */
function isLeafNode(node: unknown): boolean {
  return !hasChildObjects(node);
}

// ========================================
// CRÉATION ET TRAITEMENT DES NŒUDS
// ========================================

/**
 * Crée un objet LeafNode
 *
 * @param data - Les données du nœud feuille
 * @param path - Le chemin vers ce nœud
 * @returns L'objet LeafNode créé
 */
function createLeafNode(data: RawField, path: string[]): LeafNode {
  return {
    data: data,
    path: path,
  };
}

/**
 * Traite tous les enfants d'un nœud et collecte les feuilles
 *
 * @param node - Le nœud dont on veut traiter les enfants
 * @param currentPath - Le chemin actuel
 * @param visited - L'ensemble des nœuds visités
 * @returns Les feuilles trouvées dans les enfants
 */
function processNodeChildren(
  node: object,
  currentPath: string[],
  visited: WeakSet<object>,
): LeafNode[] {
  const childrenLeaves: LeafNode[] = [];

  // Parcourir toutes les propriétés du nœud
  for (const [propertyKey, propertyValue] of Object.entries(node)) {
    // Ignorer les propriétés à exclure
    if (shouldIgnoreProperty(propertyKey)) {
      continue;
    }

    // Traiter la propriété selon son type
    const propertyLeaves = processProperty(
      propertyKey,
      propertyValue,
      currentPath,
      visited,
    );

    childrenLeaves.push(...propertyLeaves);
  }

  return childrenLeaves;
}

/**
 * Traite une propriété et retourne les feuilles trouvées
 *
 * @param propertyKey - La clé de la propriété
 * @param propertyValue - La valeur de la propriété
 * @param parentPath - Le chemin du parent
 * @param visited - L'ensemble des nœuds visités
 * @returns Les feuilles trouvées dans cette propriété
 */
function processProperty(
  propertyKey: string,
  propertyValue: PropertyValue,
  parentPath: string[],
  visited: WeakSet<object>,
): LeafNode[] {
  // Cas 1: La propriété est un tableau
  if (Array.isArray(propertyValue)) {
    return processArrayProperty(
      propertyKey,
      propertyValue,
      parentPath,
      visited,
    );
  }

  // Cas 2: La propriété est un objet
  if (isObject(propertyValue)) {
    return processObjectProperty(
      propertyKey,
      propertyValue,
      parentPath,
      visited,
    );
  }

  // Cas 3: La propriété est une primitive (on ne fait rien)
  return [];
}

/**
 * Traite une propriété de type tableau
 *
 * @param propertyKey - La clé de la propriété
 * @param arrayValue - Le tableau à traiter
 * @param parentPath - Le chemin du parent
 * @param visited - L'ensemble des nœuds visités
 * @returns Les feuilles trouvées dans le tableau
 */
function processArrayProperty(
  propertyKey: string,
  arrayValue: unknown[],
  parentPath: string[],
  visited: WeakSet<object>,
): LeafNode[] {
  const arrayLeaves: LeafNode[] = [];

  arrayValue.forEach((item, index) => {
    if (isObject(item)) {
      // Construire le chemin avec la notation tableau : "items[0]"
      const itemPath = buildArrayItemPath(parentPath, propertyKey, index);
      const itemLeaves = findLeafObjects(item, itemPath, visited);
      arrayLeaves.push(...itemLeaves);
    }
  });

  return arrayLeaves;
}

/**
 * Traite une propriété de type objet
 *
 * @param propertyKey - La clé de la propriété
 * @param objectValue - L'objet à traiter
 * @param parentPath - Le chemin du parent
 * @param visited - L'ensemble des nœuds visités
 * @returns Les feuilles trouvées dans l'objet
 */
function processObjectProperty(
  propertyKey: string,
  objectValue: object,
  parentPath: string[],
  visited: WeakSet<object>,
): LeafNode[] {
  const objectPath = buildObjectPath(parentPath, propertyKey);
  return findLeafObjects(objectValue, objectPath, visited);
}

/**
 * Construit le chemin pour un élément de tableau
 *
 * @param parentPath - Le chemin du parent
 * @param arrayKey - La clé du tableau
 * @param index - L'index de l'élément
 * @returns Le chemin complet avec notation tableau
 */
function buildArrayItemPath(
  parentPath: string[],
  arrayKey: string,
  index: number,
): string[] {
  return [...parentPath, `${arrayKey}[${index}]`];
}

/**
 * Construit le chemin pour une propriété objet
 *
 * @param parentPath - Le chemin du parent
 * @param propertyKey - La clé de la propriété
 * @returns Le chemin complet
 */
function buildObjectPath(parentPath: string[], propertyKey: string): string[] {
  return [...parentPath, propertyKey];
}
